import { Fund, FundPortfolio } from "creso-core/types";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
} from "@tanstack/react-table";
import { useMemo } from "react";
import {
  ChartCustomLegend,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DataTable,
} from "web-design-system";
import { ChevronDown } from "lucide-react";
import { useCompareFundPortfolios } from "creso-core/hooks";
import { assetAllocationRowConfig } from "./config";
import { AssetAllocationCell } from "./asset-allocation-cell";

const columnHelper = createColumnHelper<{
  label: string;
  [key: string | number]: string | number;
}>();

export function AssetAllocation({
  funds,
  fundIds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  fundIds: string[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  const { data: portfolios } = useCompareFundPortfolios(fundIds);

  const columns = useMemo(() => {
    const firstColumn = [
      columnHelper.accessor("label", {
        header: "",
        meta: {
          className: "bg-muted px-5 py-3",
        },
        size: labelWidth,
        minSize: labelWidth,
        maxSize: labelWidth,
        cell: ({ row }) => {
          const { label } = row.original;
          const items =
            label === "Asset Allocation"
              ? [
                  {
                    label: "Equity",
                    color: "var(--color-chart-1)",
                  },
                  {
                    label: "Bond",
                    color: "var(--color-chart-2)",
                  },
                  {
                    label: "Cash",
                    color: "var(--color-chart-3)",
                  },
                  {
                    label: "Others",
                    color: "var(--color-chart-4)",
                  },
                ]
              : [
                  {
                    label: "Large Cap",
                    color: "var(--color-chart-1)",
                  },
                  {
                    label: "Mid Cap",
                    color: "var(--color-chart-2)",
                  },
                  {
                    label: "Small Cap",
                    color: "var(--color-chart-3)",
                  },
                  {
                    label: "Others",
                    color: "var(--color-chart-4)",
                  },
                ];
          return (
            <div className="flex flex-col gap-4">
              {row.original.label}
              <ChartCustomLegend items={items} />
            </div>
          );
        },
      }),
    ];

    return firstColumn
      .concat(
        fundIds.map((fundId, index) => {
          return columnHelper.accessor(fundId, {
            header: "",
            meta: {
              className: `bg-background p-0 text-sm ${
                index === funds.length - 1 ? "" : "border-r border-border"
              }`,
            },
            cell: ({ row }) => {
              const fund = funds.find((f) => f.fund_id === fundId);
              return (
                <AssetAllocationCell
                  variant={variant}
                  label={row.original.label}
                  fund={fund}
                  portfolio={
                    portfolios.find(
                      (p) => p?.fund === fund?.fund_id
                    ) as FundPortfolio
                  }
                />
              );
            },
            size: Number.MAX_SAFE_INTEGER,
          });
        })
      )
      .concat(
        variant === "default"
          ? [
              columnHelper.accessor("space", {
                header: "",
                size: 80,
                meta: {
                  className: "bg-background px-5 py-3",
                },
              }),
            ]
          : []
      );
  }, [funds, portfolios, labelWidth, fundIds, variant]);

  const data = useMemo(() => {
    return assetAllocationRowConfig.map((config) => {
      const row: { label: string; [key: string | number]: string | number } = {
        label: config.label,
      };
      return row;
    });
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  return (
    <div className="flex flex-col break-inside-avoid">
      <Collapsible
        defaultOpen
        className="w-full bg-muted rounded-lg overflow-hidden border border-border group"
      >
        <CollapsibleTrigger className="flex w-full justify-between items-center px-5 py-4">
          <span className="font-semibold text-base">Asset Allocation</span>
          {variant === "default" ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
          ) : null}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <DataTable
            pagination={false}
            table={table}
            noHeader
            className="border-none [&_table]:table-fixed"
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
